import type { SearchCriteria } from '@corratech/pylot-filters-and-sort'
import type {
    ConfigurableProductOptions,
    ConfigurableProductOptionsValues,
    Maybe
} from '@pylot-data/pylotschema'
import type { SelectedOptions } from '../../../types/SelectedOptions'

type urlFilterParamType = {
    [key: string]: string
}

/**
 * Obtains default selected options from configurable product's options and filter parameters.
 * Precedence for selecting options (color option example):
 *      if there's a selected color on PLP, always use that (only for color).
 *      If there is no, check if there's a color filter in the URL and use that.
 *      If there is no, then don't select a color. If there's only one color, select that one.
 * @param configurableOptions product configurable options
 * @param filterParams filter search parameters from URL
 * @param showFirstProductAsDefault
 * @returns default selected options object
 */
export const getDefaultSelectedOptions = (
    configurableOptions: ConfigurableProductOptions[] | undefined,
    filterParams?: SearchCriteria[],
    showFirstProductAsDefault?: boolean
): SelectedOptions => {
    if (!configurableOptions) return {}

    return configurableOptions.reduce<SelectedOptions>((acc, option) => {
        const { attribute_code, values } = option
        const matchingFilterParam = filterParams?.find(
            (param) => param.attribute_code === attribute_code
        )
        let val: string | null

        if (matchingFilterParam) {
            const swatchValue = values?.find((v) =>
                // `value_index` for magento, while `uid` for shopify
                [v?.value_index?.toString(), v?.uid].includes(
                    matchingFilterParam?.filter_value
                )
            )
            val = swatchValue?.label ?? null
        } else {
            val =
                values?.length === 1 ||
                (values?.length && showFirstProductAsDefault)
                    ? values[0]?.label!
                    : null
        }

        acc[attribute_code!] = val
        return acc
    }, {})
}
